import { get } from 'es-toolkit/compat';
import type { JSX } from 'react';
import {
	type FieldError,
	type FieldPath,
	type FieldValues,
	type UseControllerProps,
	useFormContext,
	useFormState
} from 'react-hook-form';
import { FormInput, type FormInputProps } from 'ts/components/Form';

type HookFormInputProps<
	TFieldValues extends FieldValues = FieldValues,
	TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Pick<UseControllerProps<TFieldValues, TFieldName>, 'name' | 'control' | 'rules'> &
	Omit<FormInputProps, 'name' | 'value' | 'onChange' | 'onBlur' | 'ref'>;

/**
 * This is a wrapper around FormInput that is compatible with react-hook-form using the useController hook. It requires
 * a `name` prop and optionally accepts validation `rules` and `control`. The implementation assumes that the
 * react-hook-form exposed value is compatible with the `value` prop of FormInput.
 */
export function HookFormInput<
	TFieldValues extends FieldValues = FieldValues,
	TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ name, control, rules, ...inputProps }: HookFormInputProps<TFieldValues, TFieldName>): JSX.Element {
	const { register } = useFormContext<TFieldValues>();
	const { errors } = useFormState<TFieldValues>({ name });
	const error = get(errors, name) as FieldError | undefined;
	return <FormInput {...register(name, rules)} error={error?.message || Boolean(error)} {...inputProps} />;
}
